import { FC, Fragment, ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  BAND_LABEL,
  FIT_BANDS,
  INTENT_BANDS,
  type Band,
} from "@/lib/scoring/matrix/bands";

interface MatrixGridProps {
  renderCell: (fit: Band, intent: Band) => ReactNode;
}

const AxisCaption: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: 600 }}
  >
    {children}
  </Typography>
);

/**
 * Renders the Fit×Intent grid scaffold: a centered "INTENT →" caption + column
 * headers (Low/Mid/High), a vertical "FIT ↑" caption + row headers
 * (High/Mid/Low), and a cell for each combination via `renderCell`.
 */
export const MatrixGrid: FC<MatrixGridProps> = ({ renderCell }) => (
  <Stack direction="row" spacing={1} sx={{ minHeight: 0 }}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        width: 20,
      }}
    >
      <ArrowUpwardIcon sx={{ fontSize: 12, color: "text.secondary" }} />
      <Box sx={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
        <AxisCaption>FIT</AxisCaption>
      </Box>
    </Box>

    <Box
      sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}
    >
      <Box sx={{ textAlign: "center", mb: 0.5 }}>
        <AxisCaption>INTENT →</AxisCaption>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "26px repeat(3, minmax(0, 1fr))",
            md: "44px repeat(3, minmax(0, 1fr))",
          },
          gridTemplateRows: {
            xs: "auto repeat(3, minmax(96px, auto))",
            sm: "auto repeat(3, minmax(132px, auto))",
            md: "auto repeat(3, minmax(168px, auto))",
          },
          columnGap: { xs: 0.5, sm: 1, md: 1.5 },
          rowGap: { xs: 0.5, sm: 1, md: 1.5 },
        }}
      >
        <Box />
        {INTENT_BANDS.map((b) => (
          <Box key={b} sx={{ textAlign: "center", pb: 0.5 }}>
            <AxisCaption>{BAND_LABEL[b]}</AxisCaption>
          </Box>
        ))}

        {FIT_BANDS.map((fit) => (
          <Fragment key={fit}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AxisCaption>{BAND_LABEL[fit]}</AxisCaption>
            </Box>
            {INTENT_BANDS.map((intent) => (
              <Fragment key={intent}>{renderCell(fit, intent)}</Fragment>
            ))}
          </Fragment>
        ))}
      </Box>
    </Box>
  </Stack>
);
